import { takeEvery, call, put, select, race, delay, take } from 'redux-saga/effects';
import { createFormData, requestPuppy } from '@wix/da-http-client';
import { doShake, updatedToken } from '../actions/realtime';
import { getRealtimeSlice } from '../selectors/realtime';
import { PapiRequestDamzShake } from '@wix/da-papi-types';
import ddt from '@wix/da-ddt';

function* handleDoShake(action: ReturnType<typeof doShake>) {
  try {
    const { id } = action.payload;

    const formData = createFormData<PapiRequestDamzShake>({
      id: id.toString(),
    });

    yield call(
      requestPuppy,
      {
        method: 'post',
        url: 'shake',
        data: formData,
      },
      undefined,
      'damz'
    );
  } catch (err) {
    ddt.error('damz.realtime', 'handleDoShake failed', err);
  }
}

function* waitForVia(patterns: string[], selector, predicate, timeoutMs?) {
  const current = yield select(selector);
  if (predicate(current)) {
    return current;
  }

  while (true) {
    const { timedOut } = yield race({
      hit: take(patterns),
      timedOut: timeoutMs ? delay(timeoutMs) : null,
    });

    if (timedOut) {
      ddt.log('damz.realtime', 'Timeout waiting for token');
      throw new Error('timeout');
    }

    ddt.log('damz.realtime', 'Waiting for token');
    const next = yield select(selector);
    if (predicate(next)) {
      ddt.log('damz.realtime', 'Found token');
      return next;
    }
  }
}

export function* getRealtimeToken() {
  const realtimeState: ReturnType<typeof getRealtimeSlice> = yield select(getRealtimeSlice);

  // Check if current token is still valid
  if (realtimeState.token && realtimeState.expiresAt) {
    const now = Date.now();
    if (realtimeState.expiresAt > now) {
      ddt.log('damz.realtime', 'Returning cached token');
      return realtimeState.token;
    }
  }

  // Token is expired or missing, request a new one
  const requestId = Date.now();
  ddt.log('damz.realtime', 'Requesting new token with id:', requestId);
  yield put(doShake(requestId));

  // Wait for the updatedToken action with 10s timeout
  try {
    const result = yield call(
      waitForVia,
      [`${updatedToken}`],
      getRealtimeSlice,
      state => {
        ddt.log(
          'damz.realtime',
          'Checking state:',
          state.waitingForToken,
          'vs requestId:',
          requestId,
          'token:',
          state.token
        );
        return state.waitingForToken === requestId && state.token !== undefined;
      },
      10_000 // 10 second timeout
    );

    ddt.log('damz.realtime', 'Got result:', result);
    return result.token;
  } catch (error) {
    return false;
  }
}

export function* realtimeSaga() {
  yield takeEvery(`${doShake}`, handleDoShake);
}
